import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import api from "../../utils/api";

const EmailTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({ subject: "", body: "" });
  const [viewMode, setViewMode] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    api
      .get("/email-templates")
      .then((res) => {
        setTemplates(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading templates", err);
        setIsLoading(false);
        setSnack({
          open: true,
          message: "Failed to load templates",
          severity: "error",
        });
      });
  }, []);

  const handleSelect = (template) => {
    setSelected(template);
    setForm({
      subject: template.subject || "",
      body: template.body || "",
    });
    setViewMode("edit");
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!selected) return;

    setSaving(true);

    try {
      await api.put(`/email-templates/${selected.id}`, {
        subject: form.subject,
        body: form.body,
        name: selected.name,
        module: selected.module,
        is_active: selected.is_active,
      });

      setTemplates((prev) =>
        prev.map((tpl) =>
          tpl.id === selected.id
            ? { ...tpl, subject: form.subject, body: form.body }
            : tpl
        )
      );

      setSelected((prev) =>
        prev ? { ...prev, subject: form.subject, body: form.body } : prev
      );

      setSnack({
        open: true,
        message: "Template saved successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to save template", err);
      setSnack({
        open: true,
        message: "Failed to save template",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleViewToggle = (_, newValue) => {
    if (newValue !== null) setViewMode(newValue);
  };

  const pageCardSx = {
    maxWidth: 1250,
    mx: "auto",
    mt: 1,
    p: { xs: 2, md: 3 },
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const panelCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: "rgba(255,255,255,0.96)",
    overflow: "hidden",
    height: "100%",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const toggleButtonSx = {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: "10px !important",
    px: 2,
    py: 0.9,
    color: "#2f63ad",
    borderColor: "rgba(47,99,173,0.18)",
    "&.Mui-selected": {
      backgroundColor: "rgba(47,99,173,0.1)",
      color: "#1f4f94",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "rgba(47,99,173,0.16)",
    },
  };

  if (isLoading) {
    return (
      <Box textAlign="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={pageCardSx}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Email Templates
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          View, edit, and preview email templates used throughout the app.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={panelCardSx}>
            <Box
              sx={{
                px: 2,
                py: 2,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#223142" }}
              >
                Templates
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.25 }}>
                Select a template to edit.
              </Typography>
            </Box>

            <List disablePadding>
              {templates.length > 0 ? (
                templates.map((tpl, index) => (
                  <React.Fragment key={tpl.id}>
                    <ListItemButton
                      selected={selected?.id === tpl.id}
                      onClick={() => handleSelect(tpl)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        alignItems: "flex-start",
                        "&.Mui-selected": {
                          backgroundColor: "rgba(47,99,173,0.08)",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(47,99,173,0.12)",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.2 }}>
                        <EmailIcon fontSize="small" sx={{ color: "#2f63ad" }} />
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Typography sx={{ fontWeight: 600, color: "#223142" }}>
                            {tpl.name}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            sx={{ color: "#6b778c", mt: 0.25 }}
                          >
                            {tpl.module}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    {index < templates.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ px: 2, py: 3 }}>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    No templates found.
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ ...panelCardSx, p: { xs: 2, md: 3 } }}>
            {selected ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#223142",
                        mb: 0.5,
                      }}
                    >
                      Edit Template
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      {selected.name} • {selected.module}
                    </Typography>
                  </Box>

                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewToggle}
                    aria-label="View Mode"
                    sx={{
                      gap: 1,
                      flexWrap: "wrap",
                      "& .MuiToggleButtonGroup-grouped": {
                        borderRadius: "10px !important",
                        border: "1px solid rgba(47,99,173,0.18) !important",
                      },
                    }}
                  >
                    <ToggleButton value="edit" aria-label="Edit" sx={toggleButtonSx}>
                      <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                      Edit
                    </ToggleButton>

                    <ToggleButton
                      value="preview"
                      aria-label="Preview"
                      sx={toggleButtonSx}
                    >
                      <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
                      Preview
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  fullWidth
                  label="Subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  sx={fieldSx}
                />

                {viewMode === "edit" ? (
                  <TextField
                    fullWidth
                    label="Body (HTML)"
                    name="body"
                    value={form.body}
                    onChange={handleChange}
                    multiline
                    minRows={14}
                    sx={{
                      ...fieldSx,
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255,255,255,0.96)",
                        borderRadius: "10px",
                        alignItems: "flex-start",
                      },
                    }}
                  />
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      mt: 1.5,
                      p: 2.5,
                      minHeight: 360,
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(47,99,173,0.12)",
                      boxShadow: "inset 0 1px 2px rgba(0,0,0,0.03)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#6b778c",
                        mb: 1.5,
                        fontWeight: 600,
                      }}
                    >
                      Preview
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    <Box
                      sx={{
                        "& img": { maxWidth: "100%" },
                        "& table": { maxWidth: "100%" },
                        "& p": { marginTop: 0 },
                      }}
                    >
                      <div dangerouslySetInnerHTML={{ __html: form.body }} />
                    </Box>
                  </Paper>
                )}

                <Box
                  sx={{
                    mt: 2.5,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={<SaveIcon />}
                    sx={primaryButtonSx}
                  >
                    {saving ? "Saving..." : "Save Template"}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  minHeight: 420,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 2,
                }}
              >
                <Box>
                  <EmailIcon
                    sx={{
                      fontSize: 42,
                      color: "rgba(47,99,173,0.45)",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
                  >
                    No template selected
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Select a template from the left to view and edit it.
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EmailTemplateManager;